import type {
  CellViolationInfo,
  DetailedViolation,
  ViolationGroupInfo,
} from "./types";

export type ViolationCellTarget = {
  readonly nurseId: string;
  readonly dayIndex: number;
  readonly cellInfo: CellViolationInfo;
};

const CELL_KEY_PATTERN = /^(.*)-(-?\d+)$/;

function parseCellKey(key: string): { nurseId: string; dayIndex: number } | null {
  const match = CELL_KEY_PATTERN.exec(key);
  if (!match) return null;

  const nurseId = match[1];
  const dayIndex = Number(match[2]);
  if (!nurseId || !Number.isInteger(dayIndex)) return null;

  return { nurseId, dayIndex };
}

function isManpowerViolation(type: string): boolean {
  return type === "manpower_shortage" || type === "manpower_excess";
}

function hasTypedMessage(cellInfo: CellViolationInfo, type: string): boolean {
  return cellInfo.typedMessages.some((message) => message.type === type);
}

function hasGroup(cellInfo: CellViolationInfo, detail: DetailedViolation): boolean {
  return cellInfo.groups.some((group) => isMatchingGroup(group, detail));
}

function isMatchingGroup(
  group: ViolationGroupInfo,
  detail: DetailedViolation
): boolean {
  if (group.type !== detail.type) return false;

  if (detail.type === "banned_day_after_eve" && detail.pattern) {
    return group.pattern === detail.pattern;
  }

  return true;
}

function isDayCoveredByDetail(
  detail: DetailedViolation,
  dayIndex: number
): boolean {
  if (isManpowerViolation(detail.type)) {
    return detail.day === dayIndex;
  }

  if (detail.type === "banned_day_after_eve") {
    return detail.day === dayIndex || detail.next_day === dayIndex;
  }

  if (detail.type === "max_nig_per_month") {
    return true;
  }

  const startDay = detail.start_day;
  const endDay = detail.off_end_day ?? detail.end_day;
  if (typeof startDay === "number" && typeof endDay === "number") {
    return dayIndex >= startDay && dayIndex <= endDay;
  }

  if (typeof detail.day === "number") {
    return detail.day === dayIndex;
  }

  return true;
}

export function getViolationCellTargets(
  detail: DetailedViolation,
  cellViolationMap?: ReadonlyMap<string, CellViolationInfo> | null
): ViolationCellTarget[] {
  if (!cellViolationMap || cellViolationMap.size === 0) return [];

  const targets: ViolationCellTarget[] = [];

  cellViolationMap.forEach((cellInfo, key) => {
    const parsed = parseCellKey(key);
    if (!parsed) return;

    if (detail.nurse_id && parsed.nurseId !== detail.nurse_id) return;
    if (!isDayCoveredByDetail(detail, parsed.dayIndex)) return;

    const isSameViolation =
      hasTypedMessage(cellInfo, detail.type) || hasGroup(cellInfo, detail);
    if (!isSameViolation) return;

    targets.push({
      nurseId: parsed.nurseId,
      dayIndex: parsed.dayIndex,
      cellInfo,
    });
  });

  return targets.sort((a, b) => {
    if (a.nurseId === b.nurseId) return a.dayIndex - b.dayIndex;
    return a.nurseId.localeCompare(b.nurseId);
  });
}
